import moment from 'moment/moment';
import { parseAtValue } from '#app/helpers/dateFieldTransforms';
import { FixDate } from 'finhelper';
import NumberConstants from '#app/helpers/NumberConstants';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import { getChartBiggestValue, getChartData } from './salaryPlanUtils';

// Конфигурация графика
const setupChartConfig = (am4lang_ru_RU, chart) => {
  chart.zoomOutButton.disabled = true;
  chart.logo.disabled = true;
  chart.language.locale = am4lang_ru_RU;
  chart.numberFormatter.format = NumberConstants.formatChartNumber;
  chart.colors.step = 2;
  chart.maskBullets = false;
};

// Настройка данных
const setupData = (chart, client, props) => {
  chart.data = getChartData(
    client,
    props.items,
    props.type,
    props.incomeField,
    props.retiredAge,
    props.deadAge
  );
};

// Настройка оси дат
const setupDateAxis = (dateAxis) => {
  dateAxis.fontSize = '12px';
  dateAxis.dateFormatter.dateFormat = 'YYYY-MM-DD';
  dateAxis.renderer.minGridDistance = 50;
  dateAxis.baseInterval = { count: 1, timeUnit: 'year' };
  dateAxis.gridIntervals.setAll([
    { timeUnit: 'year', count: 1 },
    { timeUnit: 'year', count: 5 },
    { timeUnit: 'year', count: 10 },
  ]);
  dateAxis.renderer.grid.template.disabled = false;
  dateAxis.renderer.grid.template.strokeDasharray = 'dotted';
  dateAxis.renderer.fullWidthTooltip = true;
  dateAxis.tooltip.dy = 25;
  dateAxis.tooltip.disabled = true;
  dateAxis.renderer.grid.template.location = 0.5;

  dateAxis.events.on('startendchanged', handleDateAxisChange);
};

// Настройка оси значений
const setupValueAxis = (valueAxis, biggestValue) => {
  valueAxis.tooltip.disabled = true;
  valueAxis.renderer.labels.template.disabled = true;
  valueAxis.renderer.grid.template.disabled = false;
  valueAxis.renderer.grid.template.strokeDasharray = 'dotted';
  valueAxis.tooltip.disabled = false;
  valueAxis.renderer.minGridDistance = 75;
  valueAxis.min = 0;
  valueAxis.max = biggestValue;
};

// Создание диапазонов осей
const createAxisRanges = (am4core, dateAxis, client, props) => {
  const retiredDate = parseAtValue(client?.birth_at).add(props.retiredAge, 'year');

  // Сегодняшний день
  const todayRange = dateAxis.axisRanges.create();
  todayRange.date = FixDate.getNowDate(client, props.tutorial).toDate();
  todayRange.grid.strokeWidth = 2;
  todayRange.grid.strokeOpacity = 1;
  todayRange.grid.stroke = am4core.color('#4378FF');
  todayRange.label.text = 'Сегодня';
  todayRange.label.fontSize = '12px';
  todayRange.label.inside = true;
  todayRange.label.color = am4core.color('#4378FF');
  todayRange.label.rotation = -90;
  todayRange.label.horizontalCenter = 'left';
  todayRange.label.verticalCenter = 'top';

  // Пенсия
  const retirementRange = dateAxis.axisRanges.create();
  retirementRange.date = retiredDate.clone().add(1, 'year').toDate();
  retirementRange.grid.strokeWidth = 2;
  retirementRange.grid.strokeOpacity = 1;
  retirementRange.grid.stroke = am4core.color('#7B7B7B');
  retirementRange.label.text = 'Пенсия';
  retirementRange.label.fontSize = '12px';
  retirementRange.label.inside = true;
  retirementRange.label.color = am4core.color('#7B7B7B');
  retirementRange.label.rotation = -90;
  retirementRange.label.horizontalCenter = 'left';
  retirementRange.label.verticalCenter = 'top';
};

// Настройка серии доходов
const createIncomeSeries = (am4core, am4charts, chart, valueAxis) => {
  const series = chart.series.push(new am4charts.LineSeries());
  series.stroke = am4core.color('#4378FF');
  series.dataFields.valueY = 'income';
  series.dataFields.dateX = 'date';
  series.yAxis = valueAxis;
  series.name = 'Доходы';
  series.strokeWidth = 2;
  series.propertyFields.strokeDasharray = 'dashLength';
  series.tooltipText = 'Доходы: {valueY}';
  series.showOnInit = true;
  series.tooltip.disabled = true;
  series.smoothing = 'monotoneX';
  return series;
};

// Настройка серии чистых доходов
const createClearSeries = (am4core, am4charts, chart, valueAxis) => {
  const series = chart.series.push(new am4charts.LineSeries());
  series.stroke = am4core.color('#4378FF');
  series.dataFields.valueY = 'clear';
  series.dataFields.dateX = 'date';
  series.yAxis = valueAxis;
  series.name = 'Чистыми';
  series.strokeDasharray = '6,6';
  series.strokeWidth = 2;
  series.propertyFields.strokeDasharray = 'dashLength';
  series.tooltipText = 'Доходы: {valueY}';
  series.showOnInit = true;
  series.tooltip.disabled = true;
  series.smoothing = 'monotoneX';
  return series;
};

// Настройка серии между линиями
const createBetweenSeries = (am4core, am4charts, chart) => {
  const series = chart.series.push(new am4charts.LineSeries());
  series.stroke = am4core.color('#4378FF');
  series.fill = am4core.color('#EAF9FF');
  series.dataFields.dateX = 'date';
  series.dataFields.openValueY = 'open';
  series.dataFields.valueY = 'close';
  series.tooltip.disabled = true;
  series.sequencedInterpolation = true;
  series.fillOpacity = 0.3;
  series.transitionDuration = 1000;
  series.smoothing = 'monotoneX';
  series.hiddenInLegend = true;
  return series;
};

// Настройка серии расходов
const createOutcomeSeries = (am4core, am4charts, chart, valueAxis) => {
  const series = chart.series.push(new am4charts.LineSeries());
  series.stroke = am4core.color('#7B7B7B');
  series.dataFields.valueY = 'outcome';
  series.dataFields.dateX = 'date';
  series.yAxis = valueAxis;
  series.name = 'Расходы';
  series.strokeWidth = 2;
  series.propertyFields.strokeDasharray = 'dashLength';
  series.tooltipText = 'Расходы: {valueY}';
  series.showOnInit = true;
  series.tooltip.disabled = true;
  series.smoothing = 'monotoneX';
  return series;
};

// Создание интерактивных точек
const createBullet = (am4core, am4charts, series, color) => {
  const bullet = series.bullets.push(new am4charts.CircleBullet());
  bullet.createChild(am4core.Rectangle);
  bullet.circle.fill = am4core.color(color);
  bullet.circle.strokeWidth = 2;
  bullet.stroke = am4core.color('#fff');
  bullet.draggable = true;

  const hoverState = bullet.states.create('hover');
  hoverState.properties.scale = 1.2;

  return bullet;
};

// Обработчики событий для точек
const setupBulletEvents = (bullet, valueAxis, updateCallback, type) => {
  bullet.events.on('dragged', (event) => {
    const target = event.target;
    target.moveTo({ x: target.pixelX, y: target.pixelY }, undefined, undefined, true);
    target.dataItem.valueY = valueAxis.yToValue(target.pixelY);
    target.dataItem.dataContext.newValue = target.dataItem.valueY;
  });

  bullet.events.on('dragstop', (event) => {
    const target = event.target;
    const year = moment(target.dataItem.dataContext.date, 'YYYY-MM-DD').format('YYYY');
    const value = target.dataItem.valueY > 0 ? target.dataItem.valueY : 0;
    const { id, isRetire } = target.dataItem.dataContext;

    updateCallback(year, value, id, isRetire);
  });
};

// Настройка интерактивности
const setupInteractions = (am4core, am4charts, chart) => {
  chart.legend = new am4charts.Legend();
  chart.cursor = new am4charts.XYCursor();
  chart.cursor.fullWidthLineX = false;
  chart.cursor.xAxis = chart.xAxes.getIndex(0);
  chart.cursor.lineX.strokeOpacity = 0;
  chart.cursor.lineX.fill = am4core.color('#000');
  chart.cursor.lineX.fillOpacity = 0.1;
  chart.cursor.behavior = 'none';

  // Скроллбар
  chart.scrollbarX = new am4core.Scrollbar();
  chart.scrollbarX.parent = chart.chartAndLegendContainer;
  chart.scrollbarX.minHeight = 8;
  chart.scrollbarX.startGrip.icon.disabled = true;
  chart.scrollbarX.endGrip.icon.disabled = true;
};

// Настройка зума
const setupZoom = (chart) => {
  const applyZoom = () => {
    const startZoom = localStorage.getItem(LocalStorageHelper.SALARY_PLAN_ZOOM_START);
    const endZoom = localStorage.getItem(LocalStorageHelper.SALARY_PLAN_ZOOM_END);

    if (startZoom && endZoom) {
      const startZoomDate = parseInt(JSON.parse(startZoom));
      const endZoomDate = parseInt(JSON.parse(endZoom));

      if (startZoomDate < endZoomDate) {
        chart.xAxes.getIndex(0).zoomToValues(startZoomDate, endZoomDate, false, true);
      }
    }
  };

  chart.events.on('ready', applyZoom);
  chart.events.on('datavalidated', applyZoom);
};

// Обработчик изменения оси дат
const handleDateAxisChange = (ev) => {
  const start = ev.target.minZoomed;
  const end = ev.target.maxZoomed;

  if (start && end && start < end && start !== end) {
    localStorage.setItem(LocalStorageHelper.SALARY_PLAN_ZOOM_START, JSON.stringify(start));
    localStorage.setItem(LocalStorageHelper.SALARY_PLAN_ZOOM_END, JSON.stringify(end));
  }
};

// Основная функция создания графика
export const createChart = (am4core, am4charts, am4lang_ru_RU, chartId, client, props) => {
  try {
    const chart = am4core.create(chartId, am4charts.XYChart);

    // Настройка базовой конфигурации
    setupChartConfig(am4lang_ru_RU, chart);
    setupData(chart, client, props);

    // Настройка осей
    const biggestValue = getChartBiggestValue(chart.data);
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

    setupDateAxis(dateAxis);
    setupValueAxis(valueAxis, biggestValue);
    createAxisRanges(am4core, dateAxis, client, props);

    // Создание серий
    const incomeSeries = createIncomeSeries(am4core, am4charts, chart, valueAxis);
    // const clearSeries = createClearSeries(am4core, am4charts, chart, valueAxis);
    // const betweenSeries = createBetweenSeries(am4core, am4charts, chart);
    const outcomeSeries = createOutcomeSeries(am4core, am4charts, chart, valueAxis);

    // Добавление интерактивности если разрешено
    if (props.allowEdit) {
      const incomeBullet = createBullet(am4core, am4charts, incomeSeries, '#4378FF');
      const outcomeBullet = createBullet(am4core, am4charts, outcomeSeries, '#7B7B7B');

      setupBulletEvents(incomeBullet, valueAxis, props.incomeUpdateValue, 'income');
      setupBulletEvents(outcomeBullet, valueAxis, props.outcomeUpdateValue, 'outcome');
    }

    // Настройка взаимодействий и зума
    setupInteractions(am4core, am4charts, chart);
    setupZoom(chart);

    return chart;
  } catch (e) {
    console.error('Error creating chart:', e);
    return null;
  }
};

// Функция обновления графика
export const updateChart = (am4core, chart, client, props) => {
  if (!chart) {
    console.warn('Chart not found, cannot update');
    return;
  }

  try {
    // Обновление данных
    chart.data = getChartData(
      client,
      props.items,
      props.type,
      props.incomeField,
      props.retiredAge,
      props.deadAge
    );

    // Обновление максимального значения оси
    const biggestValue = getChartBiggestValue(chart.data);
    chart.yAxes.getIndex(0).max = biggestValue;

    // Обновление диапазонов осей
    const dateAxis = chart.xAxes.getIndex(0);
    dateAxis.axisRanges.clear();
    createAxisRanges(am4core, dateAxis, client, props);
  } catch (e) {
    console.error('Error updating chart:', e);
  }
};

// Функция освобождения ресурсов
export const disposeChart = (chart) => {
  if (chart) {
    try {
      chart.dispose();
    } catch (e) {
      console.error('Error disposing chart:', e);
    }
  }
};
